import { useEffect, useState } from 'react';
import {
  collection,
  documentId,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {
  DailyAggregateSchema,
  type DailyAggregate,
  type Period,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

const TAG = 'admin/finance/useDailyAggregates';

export type DailyAggregatesState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | { kind: 'READY'; aggregates: DailyAggregate[] };

/**
 * Lê os agregados diários globais de `/aggregations/daily/days/{yyyymmdd}` no intervalo de um
 * `Period`, uma vez por mudança de período.
 *
 * Origem: sem issue — commit 498e432c6, 2026-05-04 15:52 +0200 (rota /dashboard/finance).
 *
 * Por quê: `getDocs`, não `onSnapshot` — o agregado muda 1×/dia pelo cron
 * (`aggregateDailyMetrics`, 04:00 BRT), real-time não vale a tubulação WebSocket. A query é
 * range por `documentId()`: o id `yyyymmdd` ordena lexicograficamente igual ao cronológico,
 * e range só por id não exige índice composto. Teto do `Period` é 365 dias (preset M12), cabe
 * numa query só.
 *
 * Contrato: estado `LOADING | ERROR | READY`; doc que falha no `DailyAggregateSchema` é
 * IGNORADO e reportado ao PostHog (`admin_daily_aggregate_parse_failed`; falha da query →
 * `admin_daily_aggregates_query_failed`). Até 2026-10-13 era só `console.warn`, sem telemetria
 * (#798 → #935 item 14). Resultado ordenado por `yyyymmdd` asc. O `guard.cancelled` descarta resposta de
 * período que já mudou. `period.from/to` chegam `yyyy-mm-dd` e viram `yyyymmdd` aqui.
 *
 * Usado por: `useFinanceMetrics` (período atual e anterior), e por ele `FinanceDashboardPage`,
 * `KpiCards`, `RevenueByCurrencyTable` (`apps/admin`).
 *
 * Arquitetura: leitura direta super-admin (rule `match /aggregations/{type}/{subcoll}/{id}`:
 * read `isSuperAdmin()`, write `false`). Escrita só pela Function.
 *
 * Dados: `DailyAggregateSchema` em `packages/types/src/aggregations.ts`; valores em centavos,
 * dia em `America/Sao_Paulo`; `labels.printedToday` é `null` nos dias anteriores ao deploy
 * da métrica (ver `labelsHasPreDeployGap` em `useFinanceMetrics`).
 */
export function useDailyAggregates(period: Period): DailyAggregatesState {
  const [state, setState] = useState<DailyAggregatesState>({ kind: 'LOADING' });

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });

    const fromCompact = period.from.replace(/-/g, '');
    const toCompact = period.to.replace(/-/g, '');

    /** Range por `documentId()` + Zod; respeita `guard.cancelled` em todo retorno. */
    async function load(): Promise<void> {
      try {
        const ref = collection(firestore, 'aggregations', 'daily', 'days');
        const q = query(
          ref,
          where(documentId(), '>=', fromCompact),
          where(documentId(), '<=', toCompact),
        );
        const snap = await getDocs(q);
        if (guard.cancelled) return;

        const aggregates: DailyAggregate[] = [];
        for (const doc of snap.docs) {
          const parsed = DailyAggregateSchema.safeParse(doc.data());
          if (parsed.success) {
            aggregates.push(parsed.data);
          } else {
            // #935 (item 14, 2026-10-13 11:00 +0200): só `console.*` não chega até nós — o admin
            // não manda DevTools. O report leva a falha ao PostHog (CLAUDE.md, Observabilidade).
            reportNonFatal('admin_daily_aggregate_parse_failed', {
              docId: doc.id,
              issues: JSON.stringify(parsed.error.issues).slice(0, 1000),
            });
          }
        }
        aggregates.sort((a, b) => a.yyyymmdd.localeCompare(b.yyyymmdd));
        setState({ kind: 'READY', aggregates });
      } catch (err) {
        if (guard.cancelled) return;
        reportNonFatal('admin_daily_aggregates_query_failed', { tag: TAG, ...serializeError(err) });
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }

    void load();

    return (): void => {
      guard.cancelled = true;
    };
  }, [period.from, period.to]);

  return state;
}
